<template>
  <div v-if="model.generalInformation.type">
    <custom-separator
      id="custom-separator-line"
      :options="getSeparatorOptions"
      class="my-4 custom-separator-line"
      @selected="onStudentFilterChanged"
    />
    <students-header-filter
      v-if="!readOnly"
      :is-individual="isIndividual"
      :key="currentStudentFilterType"
      :show-add-student-button="showAddStudentButton"
      :student-count="pagination.totalItemCount"
      :text-count="textCount"
      @show-add-student="showAddStudent = $event"
      @on-search="onSearch"
    >
      <add-student
        v-if="showAddStudentSection && !readOnly"
        :resources="collections.resources"
        @on-added="onAddStudent($event)"
        @cancel="showAddStudent = $event"
      />
    </students-header-filter>

    <custom-result-not-found
      v-if="!students.length"
      class="mt-4 row pt-3"
      text="No hay información disponible."
    />

    <student-result-row
      v-for="student in students"
      :key="student.studentSieId"
      :value="student"
      :collections="collections"
      :show-actions="true"
      :show-remove-action="isAssignedStudentsTabActive"
      :show-assign-action="isAvailableStudentsTabActive"
      :current-page="pagination.currentPage"
      :readOnly="readOnly"
      @on-removed="onRemoveHandler($event)"
      @on-assigned="onAddStudent($event)"
    />

    <pagination
      v-if="pagination.totalItemCount > pagination.perPage"
      :data="pagination"
      :page-size="pagination.perPage"
      parent-id="custom-separator-line"
      @pagination-go-page="doSearch"
    />
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomSeparator from 'custom-separator';
import Pagination from 'pagination';
import CustomResultNotFound from 'custom-result-not-found';
import StudentResultRow from './components/StudentResultRow';
import StudentsHeaderFilter from './components/StudentsHeaderFilter';
import AddStudent from './components/AddStudent';
import alert from '@/mixin/alert.mixin';
import resourcesService from '@/services/resources';
import ServiceTransitionStudent from '@/services/pei/transitionStudent.js';
import StudentStatuses from '@/utils/constants/student/studentStatuses';
import academicYearService from '@/services/academicyear';
import userRole from '@/utils/constants/userRoles';

const STUDENT_GRADE = {
  Sixth: 8,
  Twelveth: 14,
  EEA: 32,
};

const TYPE = {
  Student: 1,
  Group: 2,
};

const STUDENT_FILTER_TYPES = {
  ASSIGNED: 'ASSIGNED',
  AVAILABLE: 'AVAILABLE',
};

export default {
  name: 'Management',
  mixins: [alert],
  components: {
    ActionButton,
    CustomSeparator,
    Pagination,
    CustomResultNotFound,
    StudentResultRow,
    StudentsHeaderFilter,
    AddStudent,
  },
  props: {
    model: {
      type: Object,
      default: {
        generalInformation: {
          activity: null,
          date: null,
          description: null,
          type: { id: null, name: null },
        },
        students: {
          available: [],
          filteredStudents: [],
          assignedStudents: [],
        },
      },
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    dateAcademicYearId: null,
  }),
  watch: {
    readOnly: {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          this.currentStudentFilterType = STUDENT_FILTER_TYPES.ASSIGNED;
          this.doSearch();
        }
      },
    },
    'model.generalInformation.type': {
      immediate: true,
      async handler(newVal, previous) {
        if (newVal != null && newVal != undefined && previous != null) {
          if (
            previous.id != newVal.id &&
            newVal.id == TYPE.Student &&
            this.model.students.assignedStudents.length > 1
          ) {
            let response = await this.ShowCancelModal(
              'Confirmación',
              '¿Se removerán los estudiantes asignados por Grupo, está seguro que desea continuar?',
              'Si',
              'No'
            );

            if (!response) {
              this.model.generalInformation.type = previous;
              return;
            }

            this.model.students.assignedStudents = [];
          }
        }
        this.doSearch();
      },
    },
    'model.generalInformation.date': {
      immediate: true,
      async handler(newVal, previous) {
        if (!newVal) {
          return;
        }

        const {
          data: academicYear,
        } = await academicYearService.getAcademicYearForDate(newVal);

        this.dateAcademicYearId = academicYear.id;

        const studentsOtherYear = this.model.students.assignedStudents.some(
          (x) => x.academicYearId != this.dateAcademicYearId
        );

        if (this.isTeacher && studentsOtherYear) {
          let response = await this.ShowCancelModal(
            'Confirmación',
            '¿Se removerán los estudiantes asignados, está seguro que desea continuar?',
            'Si',
            'No'
          );

          if (!response) {
            this.model.generalInformation.date = previous;
            return;
          }

          this.model.students.assignedStudents = [];
        }

        this.doSearch();
      },
    },
  },
  data() {
    return {
      separatorOptions: [],
      currentStudentFilterType: STUDENT_FILTER_TYPES.ASSIGNED,
      searchField: null,
      showAddStudent: false,
      pagination: {
        perPage: 10,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
      },
      collections: {
        resources: [],
      },
    };
  },
  methods: {
    onAddStudent(student) {
      const validStatus = [StudentStatuses.Elegible, StudentStatuses.Servido];

      const status =
        typeof student.status === 'object'
          ? student.status
          : { id: student.statusId, name: student.status };

      if (!validStatus.includes(status.id)) {
        this.ShowToast(
          'Alerta',
          this.collections.resources['R01210005'],
          'error'
        );
        return;
      }

      const grade = student.peiGradeId
        ? { id: student.peiGradeId, name: student.peiGrade }
        : typeof student.grade === 'object'
        ? student.grade
        : { id: student.gradeId, name: student.grade };

      if (
        !(
          grade.id >= STUDENT_GRADE.Sixth && grade.id <= STUDENT_GRADE.Twelveth
        ) &&
        grade.id != STUDENT_GRADE.EEA
      ) {
        this.ShowToast(
          'Alerta',
          'Para seleccionar el estudiante debe estar en un grado mayor o igual a 6to.',
          'error'
        );
        return;
      }

      const studentSieId = student.studentId ?? student.studentSieId;

      const index = this.model.students.assignedStudents.findIndex(
        (x) => x.studentSieId == studentSieId
      );

      if (index >= 0) {
        this.ShowToast(
          'Alerta',
          this.collections.resources['R01210009'],
          'error'
        );
        return;
      }

      const impediment =
        typeof student.impediment === 'object'
          ? student.impediment
          : { id: student.statusId, name: student.impediment };

      const newStudent = {
        academicYearId: this.dateAcademicYearId,
        studentSieId: studentSieId,
        fullName: student.fullName,
        age: student.age,
        impediment: impediment,
        status: status,
        grade: grade,
      };

      if (this.isIndividual) {
        this.model.students.assignedStudents = [];
      }

      this.model.students.assignedStudents.push(newStudent);
      this.showAddStudent = false;
      this.ShowToast(
        'Proceso exitoso.',
        this.collections.resources['R01210008'],
        'success'
      );

      if (this.isIndividual) {
        this.currentStudentFilterType = STUDENT_FILTER_TYPES.ASSIGNED;

        this.getSeparatorOptions.map((r) => {
          r.active = r.id == STUDENT_FILTER_TYPES.ASSIGNED;
          return r;
        });
      }

      this.doSearch();
    },
    onRemoveHandler(studentSieId) {
      const index = this.model.students.assignedStudents.findIndex(
        (x) => x.studentSieId == studentSieId
      );
      this.model.students.assignedStudents.splice(index, 1);
      this.ShowToast(
        'Proceso exitoso.',
        this.collections.resources['R01210013'],
        'success'
      );
      this.doSearch();
    },
    async doSearch(page = 1) {
      const studentesId = this.model.students.assignedStudents.map((x) => {
        return x.studentSieId;
      });

      const items =
        this.currentStudentFilterType == STUDENT_FILTER_TYPES.ASSIGNED
          ? this.model.students.assignedStudents
          : this.model.students.available.filter(
              (x) =>
                !studentesId.includes(x.studentSieId) &&
                x.academicYearId == this.dateAcademicYearId
            );

      this.model.students.filteredStudents = items.filter(
        (x) =>
          !this.searchField ||
          x.fullName.toLowerCase().includes(this.searchField.toLowerCase()) ||
          x.studentSieId == this.searchField
      );

      this.setPagination(page);
    },
    async searchByTeacher(page = 1) {
      const mappedFilter = {
        searchField: this.searchField,
        pageIndex: 1,
        pageSize: 100,
        schoolAlternativeLocationId: 0,
      };
      mappedFilter.fromSaee12b = true;
      await ServiceTransitionStudent.getAlternativeLocationTeacherStudents(
        mappedFilter
      ).then((res) => {
        this.model.students.available = res.data.data;
        this.setPagination();
      });
    },
    async fetchResources() {
      this.collections.resources = await resourcesService.LoadValidationMessages(
        ['R01210005', 'R01210008', 'R01210009', 'R01210013', '44']
      );
    },
    onStudentFilterChanged(filterType) {
      if (this.currentStudentFilterType === filterType) return;
      this.currentStudentFilterType = filterType;
      this.searchField = null;
      this.showAddStudent = false;
      this.doSearch();
    },
    onSearch(searchField) {
      this.searchField = searchField;
      this.doSearch();
    },
    setPagination(page = 1) {
      if (!this.model.students.filteredStudents) return;

      const pageCount = Math.ceil(
        this.model.students.filteredStudents.length / this.pagination.perPage
      );

      this.pagination = {
        ...this.pagination,
        totalItemCount: this.model.students.filteredStudents.length,
        pageCount: pageCount,
        currentPage: page,
        count: this.model.students.filteredStudents.length,
      };

      this.model.students.filteredStudents = this.model.students.filteredStudents.slice(
        (page - 1) * this.pagination.perPage,
        page * this.pagination.perPage
      );
    },
  },
  computed: {
    isTeacher() {
      return this.userRole === userRole.maestro;
    },
    getSeparatorOptions() {
      const separatorOptions = [];
      separatorOptions.push({
        id: STUDENT_FILTER_TYPES.ASSIGNED,
        name: 'Estudiantes Asignados',
        active: true,
      });

      if (
        (this.hasSchoolAccessLevel || this.hasStudentAccessLevel) &&
        !this.readOnly
      ) {
        separatorOptions.push({
          id: STUDENT_FILTER_TYPES.AVAILABLE,
          name: 'Estudiantes Disponibles',
        });
      }

      return separatorOptions;
    },
    hasSchoolAccessLevel() {
      return this.$store.getters.HasPermission('level.school');
    },
    hasStudentAccessLevel() {
      return this.$store.getters.HasPermission('level.student');
    },
    students() {
      return this.model.students.filteredStudents;
    },
    isIndividual() {
      return this.model.generalInformation.type?.id == TYPE.Student; //Individual
    },
    textCount() {
      return `Total de Estudiantes ${
        this.isAssignedStudentsTabActive ? 'Asignados:' : 'Disponibles:'
      }`;
    },
    showAddStudentButton() {
      return (
        this.currentStudentFilterType == STUDENT_FILTER_TYPES.ASSIGNED &&
        !this.showAddStudent
      );
    },
    showAddStudentSection() {
      return (
        this.currentStudentFilterType == STUDENT_FILTER_TYPES.ASSIGNED &&
        this.showAddStudent
      );
    },
    isAssignedStudentsTabActive() {
      return this.currentStudentFilterType == STUDENT_FILTER_TYPES.ASSIGNED;
    },
    isAvailableStudentsTabActive() {
      return this.currentStudentFilterType == STUDENT_FILTER_TYPES.AVAILABLE;
    },
  },
  async mounted() {
    await this.fetchResources();
    await this.doSearch();

    if (this.hasSchoolAccessLevel || this.hasStudentAccessLevel) {
      await this.searchByTeacher();
    }
  },
};
</script>

<style></style>
